import { useState, useEffect, useCallback } from 'react'
import { Card } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  History,
  ChevronLeft,
  ChevronRight,
  ChevronDown,
  ChevronUp,
  Loader2,
  Clock,
  FileUp,
  FileEdit,
  Trash2,
  AlertCircle,
  CheckCircle2,
  AlertTriangle,
  FolderSync,
} from 'lucide-react'
import { knowledgeBaseService } from '@/services/knowledge-base'
import {
  type SyncLog,
  type SyncLogStatus,
  type SyncLogListResponse,
  SYNC_LOG_STATUS_CONFIG,
  formatDuration,
} from '@/schemas/sync-log'

interface SyncHistoryProps {
  projectId: string
  kbId: string
}

export function SyncHistory({ projectId, kbId }: SyncHistoryProps) {
  const [logs, setLogs] = useState<SyncLogListResponse | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [expandedId, setExpandedId] = useState<string | null>(null)
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [page, setPage] = useState(1)

  const fetchLogs = useCallback(async () => {
    try {
      const response = await knowledgeBaseService.getSyncLogs(projectId, kbId, {
        page,
        page_size: 10,
        status: statusFilter === 'all' ? undefined : (statusFilter as SyncLogStatus),
      })
      setLogs(response)
    } catch (error) {
      console.error('Error fetching sync logs:', error)
    } finally {
      setIsLoading(false)
    }
  }, [projectId, kbId, page, statusFilter])

  useEffect(() => {
    setIsLoading(true)
    fetchLogs()
  }, [fetchLogs])

  const handleStatusFilter = (value: string) => {
    setStatusFilter(value)
    setPage(1)
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString)
    return date.toLocaleDateString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    })
  }

  const isEmpty = !isLoading && (!logs || logs.sync_logs.length === 0)

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold text-white flex items-center gap-2">
          <History className="w-5 h-5 text-blue-400" />
          Historico de Sincronizacao
        </h2>
        <Select value={statusFilter} onValueChange={handleStatusFilter}>
          <SelectTrigger className="w-[160px] bg-white/5 border-gray-700">
            <SelectValue placeholder="Filtrar por status" />
          </SelectTrigger>
          <SelectContent className="glass border-gray-700/50">
            <SelectItem value="all">Todos os status</SelectItem>
            <SelectItem value="running">Em andamento</SelectItem>
            <SelectItem value="success">Sucesso</SelectItem>
            <SelectItem value="partial">Parcial</SelectItem>
            <SelectItem value="error">Erro</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <Card variant="glass" className="overflow-hidden">
        {isLoading ? (
          <div className="flex items-center justify-center p-12">
            <Loader2 className="w-8 h-8 animate-spin text-blue-400" />
          </div>
        ) : isEmpty ? (
          <div className="flex flex-col items-center justify-center text-center p-12">
            <div className="w-16 h-16 rounded-full bg-gray-700/50 flex items-center justify-center mb-4">
              <FolderSync className="w-8 h-8 text-gray-500" />
            </div>
            <h3 className="text-lg font-medium text-white mb-2">
              Nenhuma sincronizacao encontrada
            </h3>
            <p className="text-sm text-gray-400">
              {statusFilter !== 'all'
                ? 'Nenhuma sincronizacao com o status selecionado'
                : 'As sincronizacoes com o Google Drive serao exibidas aqui'}
            </p>
          </div>
        ) : (
          <div className="divide-y divide-gray-700/50">
            {logs?.sync_logs.map((log) => (
              <SyncLogItem
                key={log.id}
                log={log}
                isExpanded={expandedId === log.id}
                onToggle={() => setExpandedId(expandedId === log.id ? null : log.id)}
                formatDate={formatDate}
              />
            ))}
          </div>
        )}
      </Card>

      {/* Pagination */}
      {logs && logs.total_pages > 1 && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-400">
            Mostrando {(logs.page - 1) * logs.page_size + 1} a{' '}
            {Math.min(logs.page * logs.page_size, logs.total)} de {logs.total} sincronizacoes
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage(page - 1)}
              disabled={page <= 1}
            >
              <ChevronLeft className="w-4 h-4 mr-1" />
              Anterior
            </Button>
            <span className="text-sm text-gray-400">
              Pagina {logs.page} de {logs.total_pages}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => setPage(page + 1)}
              disabled={page >= logs.total_pages}
            >
              Proxima
              <ChevronRight className="w-4 h-4 ml-1" />
            </Button>
          </div>
        </div>
      )}
    </div>
  )
}

// Sync Log Item Component
interface SyncLogItemProps {
  log: SyncLog
  isExpanded: boolean
  onToggle: () => void
  formatDate: (date: string) => string
}

function SyncLogItem({ log, isExpanded, onToggle, formatDate }: SyncLogItemProps) {
  const config = SYNC_LOG_STATUS_CONFIG[log.status]
  const totalFiles = log.files_created + log.files_updated + log.files_deleted
  const hasErrors =
    log.error_message || (log.error_details && Object.keys(log.error_details).length > 0)

  const StatusIcon = () => {
    switch (log.status) {
      case 'running':
        return <Loader2 className="w-4 h-4 animate-spin" />
      case 'success':
        return <CheckCircle2 className="w-4 h-4" />
      case 'partial':
        return <AlertTriangle className="w-4 h-4" />
      case 'error':
        return <AlertCircle className="w-4 h-4" />
      default:
        return null
    }
  }

  return (
    <div className="hover:bg-white/5 transition-colors">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between px-4 py-4 text-left"
      >
        <div className="flex items-center gap-4">
          {/* Status Badge */}
          <span
            className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full ${config.color}`}
          >
            <StatusIcon />
            {config.label}
          </span>

          {/* Date */}
          <div className="flex items-center gap-1.5 text-sm text-gray-400">
            <Clock className="w-3.5 h-3.5" />
            {formatDate(log.started_at)}
          </div>

          {/* Duration */}
          {log.duration_seconds !== null && (
            <span className="text-sm text-gray-500">
              ({formatDuration(log.duration_seconds)})
            </span>
          )}

          {/* Files summary */}
          {totalFiles > 0 && (
            <span className="text-sm text-gray-400">
              {totalFiles} {totalFiles === 1 ? 'arquivo' : 'arquivos'}
            </span>
          )}
        </div>

        <div className="flex items-center gap-2">
          {hasErrors && log.status !== 'error' && (
            <span className="text-xs text-amber-400">Com avisos</span>
          )}
          {isExpanded ? (
            <ChevronUp className="w-4 h-4 text-gray-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-gray-400" />
          )}
        </div>
      </button>

      {/* Expanded Details */}
      {isExpanded && (
        <div className="px-4 pb-4 space-y-4">
          {/* File Stats */}
          <div className="grid grid-cols-3 gap-4">
            <div className="flex items-center gap-2 px-3 py-2 bg-emerald-500/10 rounded-lg">
              <FileUp className="w-4 h-4 text-emerald-400" />
              <div>
                <p className="text-xs text-gray-400">Criados</p>
                <p className="text-lg font-semibold text-emerald-400">{log.files_created}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-blue-500/10 rounded-lg">
              <FileEdit className="w-4 h-4 text-blue-400" />
              <div>
                <p className="text-xs text-gray-400">Atualizados</p>
                <p className="text-lg font-semibold text-blue-400">{log.files_updated}</p>
              </div>
            </div>
            <div className="flex items-center gap-2 px-3 py-2 bg-red-500/10 rounded-lg">
              <Trash2 className="w-4 h-4 text-red-400" />
              <div>
                <p className="text-xs text-gray-400">Removidos</p>
                <p className="text-lg font-semibold text-red-400">{log.files_deleted}</p>
              </div>
            </div>
          </div>

          {/* Error Message */}
          {log.error_message && (
            <div className="flex items-start gap-2 px-3 py-2 bg-red-500/10 rounded-lg">
              <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
              <div>
                <p className="text-xs text-gray-400 mb-1">Erro</p>
                <p className="text-sm text-red-300">{log.error_message}</p>
              </div>
            </div>
          )}

          {/* Error Details */}
          {log.error_details &&
            'errors' in log.error_details &&
            Array.isArray(log.error_details.errors) &&
            log.error_details.errors.length > 0 && (
              <div className="px-3 py-2 bg-amber-500/10 rounded-lg">
                <p className="text-xs text-gray-400 mb-2">
                  Avisos ({log.error_details.errors.length})
                </p>
                <ul className="space-y-1">
                  {(log.error_details.errors as string[]).slice(0, 5).map((error, index) => (
                    <li key={index} className="text-sm text-amber-300 flex items-start gap-2">
                      <span className="text-amber-400">•</span>
                      <span>{error}</span>
                    </li>
                  ))}
                  {(log.error_details.errors as string[]).length > 5 && (
                    <li className="text-sm text-gray-400 italic">
                      ... e mais {(log.error_details.errors as string[]).length - 5} avisos
                    </li>
                  )}
                </ul>
              </div>
            )}

          {/* Meta Info */}
          <div className="flex items-center gap-4 text-xs text-gray-500">
            <span>Task ID: {log.task_id}</span>
            {log.completed_at && <span>Concluido em: {formatDate(log.completed_at)}</span>}
          </div>
        </div>
      )}
    </div>
  )
}
